import { IconRefresh } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/components/patterns/DataTable";
import { ErrorState } from "@/components/shared/error-state";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import type { ProductListingGroupSort } from "@/lib/product-listing-api";
import { cn } from "@/lib/utils";
import { PaginationFooter, ProductListingSortHeader, ProductRowsSkeleton } from "./PickerParts";
import type { ProductInventoryGate } from "./product-listing-helpers";
import { UnifiedGroupRow } from "./UnifiedGroupRow";
import type { ProductCatalogSync } from "./use-product-catalog-sync";
import type { ProductListingBrowse } from "./use-product-listing-browse";
import type { ProductSelection } from "./use-product-selection";

const SKELETON_COLUMNS = ["select", "product", "type", "status", "price", "inventory", "actions"];

interface UnifiedProductTableProps {
  browse: ProductListingBrowse;
  selection: ProductSelection;
  sync: ProductCatalogSync;
  selectionIssues: CampaignCreationInvalidProduct[];
  blockProductList: boolean;
  showInventoryRefresh: boolean;
  showBlockedProductError: boolean;
  marketplaceId: string;
  inventory: ProductInventoryGate;
}

/** Single table of variation groups that expand in place into their SKUs. */
export function UnifiedProductTable({
  browse,
  selection,
  sync,
  selectionIssues,
  blockProductList,
  showInventoryRefresh,
  showBlockedProductError,
  marketplaceId,
  inventory,
}: UnifiedProductTableProps) {
  const { t } = useTranslation("campaignCreation");
  const { groups, listings, groupSort, toggleGroupSort } = browse;
  const { manualRefreshStarted, productRefreshLoading, refreshStatus, refreshFailure } = sync;
  const { matchingSelected, matchingTotal, matchingCurrent } = selection;
  return (
    <section
      className="flex min-h-[430px] min-w-0 flex-col overflow-hidden rounded-[10px] border border-line bg-surface"
      aria-label={t("products.table.label")}
    >
      <div className="min-h-0 flex-1 overflow-auto paper-scrollbar">
        <DataTable
          density="compact"
          stickyHeader
          className="min-w-[760px] table-fixed [&_thead_th]:bg-surface"
        >
          <colgroup>
            <col className="w-11" />
            <col />
            <col className="w-24" />
            <col className="w-28" />
            <col className="w-24" />
            <col className="w-24" />
            <col className="w-11" />
          </colgroup>
          <TableHeader className="bg-surface">
            <TableRow className="hover:bg-transparent">
              <TableHead>
                <Checkbox
                  aria-label={t("products.selectAll")}
                  disabled={
                    blockProductList ||
                    manualRefreshStarted ||
                    productRefreshLoading ||
                    selection.bulkPending ||
                    !browse.bulkSelectable ||
                    !selection.matchingSettled ||
                    groups.isError ||
                    groups.isPending ||
                    groups.isPlaceholderData ||
                    browse.searchSettling
                  }
                  checked={
                    matchingCurrent &&
                    matchingSelected.length > 0 &&
                    matchingSelected.length === matchingTotal
                  }
                  indeterminate={
                    matchingCurrent &&
                    matchingSelected.length > 0 &&
                    matchingSelected.length < matchingTotal
                  }
                  onCheckedChange={(checked) => {
                    if (checked) void selection.selectFiltered();
                    else selection.removeSkus(matchingSelected.map((product) => product.sku));
                  }}
                />
              </TableHead>
              <TableHead>{t("products.table.product")}</TableHead>
              <SortableHead
                field="type"
                label={t("products.table.type")}
                sort={groupSort}
                onSort={toggleGroupSort}
              />
              <TableHead>{t("products.table.advertisingStatus")}</TableHead>
              <SortableHead
                field="price"
                label={t("products.table.price")}
                sort={groupSort}
                onSort={toggleGroupSort}
              />
              <SortableHead
                field="inventory"
                label={t("products.table.inventory")}
                sort={groupSort}
                onSort={toggleGroupSort}
              />
              <TableHead>
                {showInventoryRefresh ? (
                  <button
                    type="button"
                    className="flex size-6 items-center justify-center rounded outline-none transition-colors hover:bg-subtle focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                    aria-label={t("products.table.refreshProducts")}
                    aria-busy={manualRefreshStarted || productRefreshLoading}
                    disabled={manualRefreshStarted || productRefreshLoading}
                    onClick={sync.startManualRefresh}
                  >
                    <IconRefresh
                      className={cn("size-3 shrink-0", productRefreshLoading && "animate-spin")}
                      stroke={1.7}
                      aria-hidden
                    />
                  </button>
                ) : (
                  <span className="sr-only">{t("products.table.actions")}</span>
                )}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody aria-busy={productRefreshLoading}>
            {productRefreshLoading ? (
              Array.from(
                { length: Math.max(groups.data?.data.length ?? 0, 6) },
                (_, row) => `refresh-row-${row}`,
              ).map((row) => (
                <TableRow key={row} aria-hidden="true">
                  {SKELETON_COLUMNS.map((column) => (
                    <TableCell key={column}>
                      {column === "actions" ? null : column === "product" ? (
                        <div className="flex items-center gap-2.5">
                          <Skeleton className="size-9 shrink-0" />
                          <div className="flex flex-1 flex-col gap-1.5">
                            <Skeleton className="h-3 w-3/4" />
                            <Skeleton className="h-3 w-1/2" />
                          </div>
                        </div>
                      ) : (
                        <Skeleton className={cn("h-4 w-3/4", column === "select" && "size-4")} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : manualRefreshStarted && refreshStatus.isError ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <ErrorState
                    message={t("products.refreshStatusUnavailable")}
                    onRetry={() => void refreshStatus.refetch()}
                  />
                </TableCell>
              </TableRow>
            ) : blockProductList ? (
              <TableRow>
                <TableCell colSpan={7}>
                  {showBlockedProductError &&
                  (refreshFailure || (sync.refreshRunId && refreshStatus.isError)) ? (
                    <ErrorState
                      message={refreshFailure ?? t("products.refreshStatusUnavailable")}
                      onRetry={sync.retryBlockedRefresh}
                    />
                  ) : (
                    <ProductRowsSkeleton label={t("products.loading")} />
                  )}
                </TableCell>
              </TableRow>
            ) : groups.isError ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <ErrorState
                    message={t("products.loadError")}
                    onRetry={() => void groups.refetch()}
                  />
                </TableCell>
              </TableRow>
            ) : groups.isPending ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <ProductRowsSkeleton label={t("products.loading")} />
                </TableCell>
              </TableRow>
            ) : groups.data?.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                  {t("products.noMatches")}
                </TableCell>
              </TableRow>
            ) : null}
            {!blockProductList &&
              !productRefreshLoading &&
              !(manualRefreshStarted && refreshStatus.isError) &&
              groups.data?.data.map((group) => (
                <UnifiedGroupRow
                  key={group.variationGroupKey}
                  group={group}
                  browse={browse}
                  selection={selection}
                  selectionIssues={selectionIssues}
                  productRefreshLoading={productRefreshLoading}
                  marketplaceId={marketplaceId}
                  inventory={inventory}
                />
              ))}
          </TableBody>
        </DataTable>
      </div>
      {browse.activeGroup &&
      (listings.data?.pagination.total ?? 0) > (listings.data?.pagination.pageSize ?? 20) ? (
        <PaginationFooter
          page={browse.listingPage}
          total={listings.data?.pagination.total ?? 0}
          pageSize={listings.data?.pagination.pageSize ?? 20}
          onPageChange={browse.setListingPage}
        />
      ) : null}
      <PaginationFooter
        page={browse.groupPage}
        total={groups.data?.pagination.total ?? 0}
        pageSize={groups.data?.pagination.pageSize ?? 20}
        onPageChange={browse.setGroupPage}
      />
    </section>
  );
}

function SortableHead({
  field,
  label,
  sort,
  onSort,
}: {
  field: ProductListingGroupSort["sortBy"];
  label: string;
  sort: ProductListingGroupSort | null;
  onSort: (field: ProductListingGroupSort["sortBy"]) => void;
}) {
  return (
    <TableHead
      aria-sort={
        sort?.sortBy === field ? (sort.sortOrder === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <ProductListingSortHeader field={field} label={label} sort={sort} onSort={onSort} />
    </TableHead>
  );
}
